import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { JobRoleTitle } from "../types";

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { departments } = useData();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [mobile, setMobile] = React.useState("");
  const [name, setName] = React.useState("");
  const [jobRole, setJobRole] = React.useState("");
  const [department, setDepartment] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const jobTitles: JobRoleTitle[] = [
    'Accountant',
    'Accounts Executive',
    'Accounts Payable Officer',
    'Accounts Receivable Officer',
    'AI Engineer',
    'Allocator',
    'Backend Engineer',
    'Business Development Manager',
    'Business Development Representative',
    'Bookkeeper',
    'Cadet',
    'Chief Executive Officer',
    'Chief Marketing Officer',
    'Chief Operations Officer',
    'Commercial Director',
    'Commercial Manager',
    'Commercial Officer',
    'Commercial Team Leader',
    'Construction Manager',
    'Contracts & Claims Manager',
    'Contracts Administrator',
    'Credit & Collections Officer',
    'Chief Revenue Officer',
    'Chief Sales & Revenue Officer',
    'Chief Technology Officer',
    'Customer Success Manager',
    'Designer',
    'Draftsman',
    'Estimator',
    'Finance Manager',
    'Front End Engineer',
    'Full Stack Engineer',
    'General Manager',
    'Hire Manager',
    'HR Manager',
    'HSEQ Manager',
    'Implementation Manager',
    'Inventory Controller',
    'IT Intern',
    'IT Manager',
    'Labour Manager',
    'Labourer',
    'Leading Hand',
    'Managing Director',
    'Marketing Manager',
    'Office Admin',
    'Operations Admin',
    'Operations Coordinator',
    'Operations Director',
    'Operations Manager',
    'Paralegal',
    'Partner',
    'Payroll Officer',
    'President',
    'Principal',
    'Procurement Manager',
    'Progress Claims Manager',
    'Progress Claims Officer',
    'Project Coordinator',
    'Project Manager',
    'Project Engineer',
    'QA Engineer',
    'Receptionist',
    'Regional Manager',
    'Safety Officer',
    'Sales Assistant',
    'Sales Development Rep',
    'Sales Manager',
    'Sales Representative',
    'Sales Team Leader',
    'Scaffolder',
    'Site Manager',
    'Site Supervisor',
    'Site Engineer',
    'Site Foreman',
    'State Manager',
    'Structural Engineer',
    'Team Leader',
    'Tech Lead',
    'Technician',
    'Tender Manager',
    'Transport Manager',
    'Truck Driver',
    'Vice President',
    'Warehouse Assistant',
    'Warehouse Manager',
    'Yard Supervisor',
    'Yardsman'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    try {
      const success = await signup(email, password, mobile, jobRole, department, name);
      if (success) {
        navigate("/login");
      } else {
        toast.error("Failed to create account");
      }
    } catch (error) {
      toast.error("An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create an account</CardTitle>
          <CardDescription>Enter your details to sign up</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                placeholder="04xx xxx xxx"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select 
                onValueChange={setDepartment} 
                value={department}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.departmentCode}>
                      {dept.departmentCode} {dept.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobRole">Job Role</Label>
              <Select 
                onValueChange={setJobRole} 
                value={jobRole}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a job role" />
                </SelectTrigger>
                <SelectContent>
                  {jobTitles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating account..." : "Sign up"}
            </Button>
            <p className="text-center text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Log in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
